import Image from '@components/Image/Image';
import { CustomizedTypography } from '@components/Customized';
import { Divider, makeStyles } from '@material-ui/core';
import { requestEnumToString } from '@utils/enumToString';
import { CapaQuotationServiceType } from '@generated/graphql';
import * as assets from '@assets';
import config from '@config';
import { colorSet, theme } from '@components/Provider';
import { RFQ } from '../types';
import { memo } from 'react';
import { formatDate, isEqual } from '@utils';

type RfqProjectInfoProps = RFQ;

const RfqProjectInfo = ({ rfq }: RfqProjectInfoProps) => {
  const classes = useStyles();

  if (!rfq) return null;

  const {
    projectName,
    serviceType,
    protoQuantity,
    quantity,
    thumbnailId
  } = rfq;

  const isDesign = rfq.serviceType === CapaQuotationServiceType.Design;

  const isPcb =
    rfq.serviceType === CapaQuotationServiceType.Pcb && !rfq.quantity;

  const getThumbnail = () => {
    if (isDesign) {
      return thumbnailId
        ? `${config.fileApiUrl}/file/${thumbnailId}`
        : assets.DefaultThumbnail;
    }

    return thumbnailId
      ? `${config.fileApiUrl}/cloud/${thumbnailId}.png`
      : assets.DefaultThumbnail;
  };

  return (
    <div className={classes.root}>
      <div className="previewImage">
        {!isPcb ? (
          <Image
            src={getThumbnail() ?? ''}
            alt="thumbnail"
            onError={(e) => {
              e.currentTarget.src = assets.DefaultThumbnail;
            }}
          />
        ) : (
          <div className="pcb-design-icon">
            <Image
              src={assets.PCBDesignIcon}
              width={43}
              height={42}
              alt={'PCB 설계'}
            />
          </div>
        )}
      </div>
      <div className={classes.detail}>
        <CustomizedTypography
          className="title"
          color="textPrimary"
          noWrap
          gutterBottoms={4}
        >
          {projectName}
        </CustomizedTypography>
        <CustomizedTypography variant="body2" color="textSecondary" noWrap>
          요청서 작성일 {formatDate(rfq?.createdAt ?? '', 'YYYY.MM.DD')}
        </CustomizedTypography>
        <div>
          <CustomizedTypography variant="body2" color="textSecondary">
            {requestEnumToString(serviceType as CapaQuotationServiceType)}
          </CustomizedTypography>

          {(!!protoQuantity || !!quantity) && (
            <>
              <Divider orientation="vertical" className={classes.divider} />
              <CustomizedTypography variant="body2" color="textSecondary">
                {isDesign ? '시제품' : '수량'}&nbsp;
                <span className="quantity">
                  {isDesign
                    ? (protoQuantity ?? 0).toLocaleString('kr')
                    : (quantity ?? 0).toLocaleString('kr')}
                </span>
                개
              </CustomizedTypography>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default memo(RfqProjectInfo, ({ rfq }, { rfq: nextRfq }) => {
  const keys = [
    'projectName',
    'serviceType',
    'protoQuantity',
    'quantity',
    'thumbnailId'
  ];

  return isEqual({ source: rfq, target: nextRfq, keys });
});

const useStyles = makeStyles(
  {
    root: {
      display: 'flex',
      flexWrap: 'nowrap',
      width: '100%',
      minHeight: 80,
      '& img': {
        height: 80,
        width: 80,
        borderRadius: 16,
        border: `1px solid ${colorSet.gray400}`
      },
      '& .pcb-design-icon': {
        width: 80,
        minHeight: 80,
        borderRadius: 16,
        border: `1px solid ${colorSet.gray400}`,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        '& img': {
          height: 42,
          width: 43,
          borderRadius: 'unset',
          border: 'unset'
        }
      },
      '& .previewImage': {
        marginRight: 16
      }
    },
    detail: {
      minWidth: 0,
      width: '100%',
      '& > div': {
        display: 'flex',
        alignItems: 'center'
      },
      '& .quantity': {
        color: colorSet.primary500
      },
      '& .title': {
        ...theme.typography.h4,
        fontWeight: 700,
        width: '100%',
        marginBottom: 4
      }
    },
    divider: {
      margin: '0px 8px',
      height: 12
    }
  },
  {
    classNamePrefix: 'rfq-card__project-info'
  }
);
